import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ADMIN_PERMISSION_MODULES } from "@/lib/permissions";
import { toast } from "sonner";
import { Plus, Trash2, Edit } from "lucide-react";

const emptyPerms = ADMIN_PERMISSION_MODULES.reduce((acc, m) => {
  acc[m.key] = m.actions.reduce((a, x) => { a[x] = false; return a; }, {});
  return acc;
}, {});

export default function SubAdminsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", role_name: "Sub Admin", status: "active", permissions: emptyPerms });

  const load = () => api.get("/sub-admins").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditing(null);
    setForm({ full_name: "", email: "", password: "", role_name: "Sub Admin", status: "active", permissions: JSON.parse(JSON.stringify(emptyPerms)) });
  };

  const togglePerm = (mod, act) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [mod]: { ...f.permissions[mod], [act]: !f.permissions[mod]?.[act] } } }));
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/sub-admins/${editing.id}`, payload);
        toast.success("Sub-admin updated");
      } else {
        await api.post("/sub-admins", form);
        toast.success("Sub-admin created");
      }
      setOpen(false);
      reset();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const startEdit = (it) => {
    const perms = JSON.parse(JSON.stringify(emptyPerms));
    Object.keys(it.permissions || {}).forEach((k) => { perms[k] = { ...perms[k], ...it.permissions[k] }; });
    setEditing(it);
    setForm({ full_name: it.full_name, email: it.email, password: "", role_name: it.role_name || "Sub Admin", status: it.status, permissions: perms });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this sub-admin?")) return;
    await api.delete(`/sub-admins/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="sub-admins-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Team</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Sub-Admins</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="add-sub-admin-btn"><Plus className="w-4 h-4 mr-2" />Add Sub-Admin</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit Sub-Admin" : "Create Sub-Admin"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="sa-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sa-email" /></div>
              <div><Label>{editing ? "New Password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sa-password" /></div>
              <div><Label>Role name</Label><Input value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} /></div>
            </div>
            <div>
              <Label className="block mb-2">Permissions</Label>
              <div className="border border-slate-200 rounded-md divide-y">
                {ADMIN_PERMISSION_MODULES.map((m) => (
                  <div key={m.key} className="p-4">
                    <div className="font-semibold text-sm mb-2">{m.label}</div>
                    <div className="flex flex-wrap gap-4">
                      {m.actions.map((a) => (
                        <label key={a} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                          <Checkbox checked={!!form.permissions[m.key]?.[a]} onCheckedChange={() => togglePerm(m.key, a)} data-testid={`perm-${m.key}-${a}`} />
                          {a}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={save} data-testid="sa-save-btn">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No sub-admins yet</TableCell></TableRow> :
              items.map((it) => (
                <TableRow key={it.id} className="even:bg-slate-50">
                  <TableCell className="font-medium">{it.full_name}</TableCell>
                  <TableCell>{it.email}</TableCell>
                  <TableCell>{it.role_name || "Sub Admin"}</TableCell>
                  <TableCell><Badge variant={it.status === "active" ? "default" : "secondary"}>{it.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`edit-sa-${it.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(it.id)} data-testid={`del-sa-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
